import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const LandingPage = () => {
    const navigate = useNavigate();

    return (
        <PageLayout>
            {/* Hero Section */}
            <section className="relative py-20 lg:py-32 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5 -z-10" />
                <div className="container mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-8 animate-fade-in-up">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                        </span>
                        Join 10,000+ changemakers today
                    </div>

                    <h1 className="text-5xl lg:text-7xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
                        Make a Real Difference <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                            In Your Community
                        </span>
                    </h1>

                    <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Connect with local organizations, track your impact, and build your skills.
                        VolunteerHub makes it easy to turn your passion into action.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <Button
                            size="lg"
                            className="w-full sm:w-auto text-lg px-8 py-6 shadow-xl shadow-blue-600/20 hover:shadow-blue-600/30 transition-all transform hover:-translate-y-1"
                            onClick={() => navigate('/login')}
                            iconName="ArrowRight"
                            iconPosition="right"
                        >
                            Get Started Now
                        </Button>
                        <Button
                            variant="outline"
                            size="lg"
                            className="w-full sm:w-auto text-lg px-8 py-6 bg-white hover:bg-slate-50 border-slate-200"
                            onClick={() => navigate('/smart-opportunity-finder')}
                        >
                            Browse Opportunities
                        </Button>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-12 border-y border-slate-100 bg-slate-50/50">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="text-center">
                            <p className="text-4xl font-bold text-slate-900 mb-1">10k+</p>
                            <p className="text-slate-500 font-medium">Active Volunteers</p>
                        </div>
                        <div className="text-center">
                            <p className="text-4xl font-bold text-slate-900 mb-1">500+</p>
                            <p className="text-slate-500 font-medium">Partner Organizations</p>
                        </div>
                        <div className="text-center">
                            <p className="text-4xl font-bold text-slate-900 mb-1">150k+</p>
                            <p className="text-slate-500 font-medium">Hours Contributed</p>
                        </div>
                        <div className="text-center">
                            <p className="text-4xl font-bold text-slate-900 mb-1">$4.2M</p>
                            <p className="text-slate-500 font-medium">Community Value</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything you need to make an impact</h2>
                        <p className="text-lg text-slate-600">
                            Whether you're looking to volunteer or manage a team, we have the tools you need.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="p-8 rounded-2xl bg-white border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-xl transition-shadow group">
                            <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Icon name="Search" size={28} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">Smart Matching</h3>
                            <p className="text-slate-600 leading-relaxed">
                                Find opportunities that match your skills, interests, and schedule perfectly with our AI-powered recommendation engine.
                            </p>
                        </div>

                        <div className="p-8 rounded-2xl bg-white border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-xl transition-shadow group">
                            <div className="w-14 h-14 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Icon name="TrendingUp" size={28} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">Track Progress</h3>
                            <p className="text-slate-600 leading-relaxed">
                                Visualize your impact over time. Track hours, earn badges, and generate official certificates for your resume.
                            </p>
                        </div>

                        <div className="p-8 rounded-2xl bg-white border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-xl transition-shadow group">
                            <div className="w-14 h-14 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Icon name="Users" size={28} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">Community Hub</h3>
                            <p className="text-slate-600 leading-relaxed">
                                Connect with like-minded individuals, join groups, and share your experiences with a vibrant community.
                            </p>
                        </div>
                    </div>
                </div>
            </section>


            {/* How It Works Section */}
            <section className="py-24 bg-slate-50">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">How VolunteerHub Works</h2>
                        <p className="text-lg text-slate-600">Getting started is easy. Follow these simple steps to begin your volunteering journey.</p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8">
                        {[
                            { step: '01', title: 'Create Profile', desc: 'Sign up and tell us about your skills and interests.' },
                            { step: '02', title: 'Find Events', desc: 'Browse local opportunities that match your passion.' },
                            { step: '03', title: 'Volunteer', desc: 'show up, help out, and make a difference.' },
                            { step: '04', title: 'Track Impact', desc: 'Log your hours and watch your community impact grow.' }
                        ].map((item, idx) => (
                            <div key={idx} className="relative p-6 rounded-xl bg-white border border-slate-100 shadow-sm">
                                <div className="text-5xl font-bold text-slate-100 mb-4">{item.step}</div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2 relative z-10">{item.title}</h3>
                                <p className="text-slate-600 relative z-10">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Explore Causes */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-slate-900 text-center mb-16">Find a cause that moves you</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            'Environment', 'Education', 'Health', 'Animals',
                            'Arts & Culture', 'Community', 'Crisis Support', 'Seniors'
                        ].map((cause, idx) => (
                            <button key={idx} className="p-6 rounded-xl border border-slate-200 hover:border-blue-500 hover:shadow-lg hover:bg-blue-50 transition-all text-left group">
                                <span className="text-lg font-medium text-slate-700 group-hover:text-blue-700">{cause}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 bg-slate-50">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold text-slate-900 text-center mb-16">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        {[
                            { q: "Is VolunteerHub free to use?", a: "Yes, it is completely free for volunteers to join and find opportunities." },
                            { q: "Can I track hours for school credit?", a: "Absolutely. We provide verifiable reports that you can submit to schools or employers." },
                            { q: "How do I sign up as an organization?", a: "Partner organizations can contact our support team to get a verified account set up." }
                        ].map((faq, idx) => (
                            <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                                <h3 className="text-lg font-bold text-slate-900 mb-2">{faq.q}</h3>
                                <p className="text-slate-600">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 bg-slate-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-slate-900 text-center mb-16">Loved by volunteers everywhere</h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                            <div className="flex gap-1 text-yellow-400 mb-4">
                                {[1, 2, 3, 4, 5].map(i => <Icon key={i} name="Star" size={16} fill="currentColor" />)}
                            </div>
                            <p className="text-slate-700 mb-6 italic">"VolunteerHub helped me find a local food bank that needed my exact skills. I've been volunteering there for 6 months now and love it!"</p>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden">
                                    <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330" alt="Sarah" className="w-full h-full object-cover" />
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900 text-sm">Sarah Mitchell</p>
                                    <p className="text-slate-500 text-xs">Community Helper</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                            <div className="flex gap-1 text-yellow-400 mb-4">
                                {[1, 2, 3, 4, 5].map(i => <Icon key={i} name="Star" size={16} fill="currentColor" />)}
                            </div>
                            <p className="text-slate-700 mb-6 italic">"The tracking features are amazing. I was able to download a report of my hours to show my school counselor for credit. Super easy."</p>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden">
                                    <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e" alt="James" className="w-full h-full object-cover" />
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900 text-sm">James Wilson</p>
                                    <p className="text-slate-500 text-xs">Student Volunteer</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                            <div className="flex gap-1 text-yellow-400 mb-4">
                                {[1, 2, 3, 4, 5].map(i => <Icon key={i} name="Star" size={16} fill="currentColor" />)}
                            </div>
                            <p className="text-slate-700 mb-6 italic">"As an organizer, this platform saves me hours every week. Managing shifts and communicating with volunteers has never been simpler."</p>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden">
                                    <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2" alt="Elena" className="w-full h-full object-cover" />
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900 text-sm">Elena Rodriguez</p>
                                    <p className="text-slate-500 text-xs">Nonprofit Manager</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to start your journey?</h2>
                    <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto">
                        Join thousands of volunteers making a difference today. It only takes a minute to get started.
                    </p>
                    <Button
                        size="lg"
                        className="text-blue-600 bg-white hover:bg-slate-100 px-8 py-4 text-lg font-bold"
                        onClick={() => navigate('/login')}
                    >
                        Create Free Account
                    </Button>
                </div>
            </section>
        </PageLayout>
    );
};

export default LandingPage;
